import { ProjectDot } from "@/components/projects/project-color";
import { AppLink } from "@/components/ui/app-link";
import { Avatar } from "@/components/ui/avatar";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { type SortDir, useUrlTableSort } from "@/components/ui/sortable-table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BulkActions } from "@/components/work-items/bulk-actions";
import { WorkItemRowActions, type WorkflowAction } from "@/components/work-items/row-actions";
import { StandaloneBadge } from "@/components/work-items/standalone-badge";
import { AGENT_AVATAR_BY_USER_ID } from "@/lib/agent-avatars";
import { workItemHref } from "@/lib/project-href";
import { typeColor, typeLabel } from "@/lib/work-item-types";
import { displayStateName } from "@/lib/workflow";
import { Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowDown, ArrowUp, Calendar, Minus, Paperclip } from "lucide-react";
import { useState } from "react";

/**
 * The work-items list table — a `DataTable` configuration plus the
 * work-item specifics: multi-select with the floating bulk-edit
 * toolbar, per-row workflow actions and the description tooltip on
 * titles.
 *
 * Used by all three work-items list routes: the global `/work-items`,
 * the project tab and the GitLab-native project route. Project-scoped
 * views pass `hideProjectColumn`; the My Page cards use `compact`.
 */

function priorityIcon(priority: string) {
  switch (priority) {
    case "urgent":
      return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
    case "high":
      return <ArrowUp className="h-3.5 w-3.5 text-orange-500" />;
    case "low":
      return <ArrowDown className="h-3.5 w-3.5 text-blue-500" />;
    default:
      return <Minus className="h-3.5 w-3.5 text-gray-400" />;
  }
}

type Status = { id: string; name: string; color: string };

/** Column keys the list endpoint sorts by (`?sort=&dir=`) across all pages. */
export const WORK_ITEM_SORT_KEYS = [
  "id",
  "type",
  "status",
  "priority",
  "title",
  "project",
  "release",
  "loggedHours",
  "billing",
  "assignee",
  "due",
  "lastUpdate",
  "created",
] as const;

export type WorkItemRow = {
  id: string;
  gitlabIssueId: string;
  globalId: number;
  title: string;
  description: string | null;
  state: string;
  priority: string;
  dueDate: string | null;
  project: {
    id: string;
    name: string;
    slug: string;
    customer: { id: string; name: string } | null;
    group: { path: string } | null;
  };
  status: Status | null;
  release: { id: string; name: string; status: string } | null;
  gitlabType: string | null;
  standalone: boolean;
  assignee: { id: string; name: string | null } | null;
  author: { id: string; name: string | null } | null;
  createdAt: string;
  availableActions: WorkflowAction[];
  isWaitingOnClient: boolean;
  /** Billing shape — only a `fixed`-mode WI with a price set can be
   *  bulk-turned into a report; hours-mode WIs bill through their time
   *  entries instead. */
  billingMode: string;
  fixedPrice: string | null;
  /** Set when someone manually flagged this WI as already billed outside
   *  a Hub report — excludes it from the bulk "Create report" eligibility
   *  the same way an existing `InvoiceItem` does. */
  manuallyInvoicedAt: string | null;
  _count: { attachments: number; invoiceItems: number };
  /** Sum of every linked TimeEntry's hours, regardless of state — same
   *  rule the WI detail sidebar's "spent" total uses. */
  loggedHours: string;
  /** Newest comment or change made by a person (falls back to creation). */
  lastUpdate: { at: string; user: { id: string; name: string | null } | null } | null;
};

type Props = {
  workItems: WorkItemRow[];
  users: Array<{ id: string; name: string }>;
  /** Releases of the current project (open, locked and closed) — powers
   *  the release filter and the bulk Release action (the latter narrowed
   *  to `status: "open"` below; you can't bulk-assign into a closed
   *  release). Only the project-scoped listing passes this; on the global
   *  list releases are ambiguous (project-scoped), so both hide. */
  releases?: Array<{ id: string; name: string; status: string }>;
  /** Project-scoped views hide the project column — every row would
   *  carry the same value. */
  hideProjectColumn?: boolean;
  /** Viewer's user id — passed to the per-row action buttons so
   *  self-claim actions can default to the current user. */
  currentUserId?: string | null;
  /** Slim five-column mode used on the My Page per-project cards:
   *  only `#`, Type, Status, Title, Due — no selection checkboxes,
   *  no bulk-edit toolbar, no row actions. The full-fat listing on
   *  `/work-items` leaves this off. */
  compact?: boolean;
  /** Customer accounts — strip the bulk-action infrastructure
   *  (selection column, bulk toolbar, per-row action buttons).
   *  The row remains clickable as a title link; everything else
   *  is hidden because customers can't mutate WIs anyway. */
  isCustomer?: boolean;
  /** Admin / accountant — surfaces the "Create report" bulk action for
   *  a selection of fixed-price work items, and reveals the Billing
   *  column (hours vs. fixed price, and the agreed amount). Everyone
   *  else keeps the plain assignee/priority/release/team/delete bar.
   *  The server already redacts `fixedPrice` to `null` for anyone who
   *  isn't admin/accountant, so this is belt-and-suspenders. */
  canSeeAll?: boolean;
  /** Managers and admins may set a status directly, skipping the
   *  workflow's own path — the bulk twin of the per-ticket override. */
  canOverrideStatus?: boolean;
  /** Sort through the URL (`?sort=&dir=`) so the server orders the whole
   *  list; without it the table sorts only the rows it was given. */
  urlSort?: { sort: string | undefined; dir: SortDir | undefined };
};

/** Compact plain-text preview of a WI description for the title
 *  tooltip — collapses blank runs and caps the length so a long
 *  body can't fill the screen. Returns `null` when there's nothing
 *  worth showing. */
function descriptionPreview(description: string | null): string | null {
  const text = (description ?? "")
    .replace(/\r/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (!text) return null;
  return text.length > 500 ? `${text.slice(0, 500)}…` : text;
}

// Priority sorts by urgency, not alphabetically.
const priorityRank: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

export function WorkItemsTable({
  workItems,
  users,
  releases = [],
  hideProjectColumn = false,
  currentUserId = null,
  compact = false,
  isCustomer = false,
  canSeeAll = false,
  canOverrideStatus = false,
  urlSort,
}: Props) {
  // Customers behave like `compact` for selection / bulk / row
  // actions — they have no mutations to offer. Keep the full
  // listing columns visible (id / type / status / title / hours
  // / dates), just drop the selection + bulk infrastructure.
  const hideBulk = compact || isCustomer;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const sort = useUrlTableSort({
    sortKey: urlSort?.sort ?? null,
    sortDir: urlSort?.dir ?? "asc",
    onChange: (key, dir) =>
      navigate({
        // @ts-expect-error generic search updater, same as the filter bar.
        search: (prev: Record<string, unknown>) => ({ ...prev, sort: key, dir, page: undefined }),
      }),
  });

  const columns: Array<DataTableColumn<WorkItemRow>> = [
    {
      key: "id",
      header: "#",
      headClassName: "w-12",
      cellClassName: "font-mono text-xs text-muted-foreground",
      sortAccessor: (w) => w.globalId,
      cell: (w) => w.globalId,
    },
    {
      key: "type",
      header: "Type",
      headClassName: "w-24",
      sortAccessor: (w) => (w.standalone ? "standalone" : (w.gitlabType ?? "")),
      cell: (w) => {
        if (w.standalone) return <StandaloneBadge size="xs" />;
        if (!w.gitlabType) return <span className="text-xs text-muted-foreground">—</span>;
        const color = typeColor(w);
        return (
          <span
            className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
            style={{ borderColor: `${color}40`, backgroundColor: `${color}18`, color }}
          >
            {typeLabel(w)}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      headClassName: "w-32",
      sortAccessor: (w) => (w.status ? displayStateName(w.status.name) : ""),
      cell: (w) =>
        w.status ? (
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border whitespace-nowrap"
            style={{
              borderColor: `${w.status.color}40`,
              backgroundColor: `${w.status.color}18`,
              color: w.status.color,
            }}
          >
            {displayStateName(w.status.name)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "priority",
      header: "Pri",
      headClassName: "w-8",
      hidden: compact,
      sortAccessor: (w) => priorityRank[w.priority] ?? 2,
      cell: (w) => <span title={w.priority}>{priorityIcon(w.priority)}</span>,
    },
    {
      key: "title",
      header: "Title",
      cellClassName: "max-w-xs",
      sortAccessor: (w) => w.title.toLowerCase(),
      cell: (w) => (
        <div className="flex items-center min-w-0">
          {(() => {
            const titleLink = (
              <AppLink
                href={workItemHref(w.globalId)}
                className="hover:underline text-primary font-medium min-w-0 break-words"
              >
                {w.title}
              </AppLink>
            );
            const preview = descriptionPreview(w.description);
            if (!preview) return titleLink;
            return (
              <Tooltip>
                <TooltipTrigger asChild>{titleLink}</TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-sm whitespace-pre-wrap text-xs leading-relaxed"
                >
                  {preview}
                </TooltipContent>
              </Tooltip>
            );
          })()}
          {w._count.attachments > 0 ? (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground shrink-0 ml-1"
              title={`${w._count.attachments} attachment${w._count.attachments === 1 ? "" : "s"}`}
            >
              <Paperclip className="h-3 w-3" aria-hidden="true" />
              {w._count.attachments}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "project",
      header: "Project",
      hidden: compact || hideProjectColumn,
      sortAccessor: (w) => w.project.name.toLowerCase(),
      cell: (w) => (
        <div className="text-sm">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <ProjectDot projectId={w.project.id} />
            <Link
              to="/projects/$slug"
              params={{ slug: w.project.slug }}
              className="truncate hover:underline text-foreground"
            >
              {w.project.name}
            </Link>
          </span>
          {w.project.customer && (
            <p className="text-xs text-muted-foreground">{w.project.customer.name}</p>
          )}
        </div>
      ),
    },
    {
      key: "release",
      header: "Release",
      hidden: compact,
      sortAccessor: (w) => w.release?.name?.toLowerCase() ?? null,
      cell: (w) =>
        w.release ? (
          <span className="text-sm">
            {w.release.name}
            {w.release.status !== "open" && (
              <span className="text-muted-foreground"> ({w.release.status})</span>
            )}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "loggedHours",
      header: "Logged",
      headClassName: "w-20",
      hidden: compact,
      sortAccessor: (w) => Number(w.loggedHours),
      cell: (w) =>
        Number(w.loggedHours) > 0 ? (
          <span className="text-sm">
            {Number(w.loggedHours).toLocaleString("cs-CZ", { maximumFractionDigits: 2 })}h
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "billing",
      header: "Billing",
      headClassName: "w-32",
      hidden: compact || !canSeeAll,
      sortAccessor: (w) => w.billingMode,
      cell: (w) =>
        w.billingMode === "fixed" ? (
          <span className="text-sm">
            Fixed price
            {w.fixedPrice ? ` — ${Number(w.fixedPrice).toLocaleString("cs-CZ")}` : ""}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">By hours</span>
        ),
    },
    {
      key: "assignee",
      header: "Assignee",
      hidden: compact || isCustomer,
      sortAccessor: (w) => w.assignee?.name?.toLowerCase() ?? null,
      cell: (w) =>
        w.assignee?.name ? (
          <span className="text-sm">{w.assignee.name}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "due",
      header: "Due",
      headClassName: "w-24",
      sortAccessor: (w) => (w.dueDate ? new Date(w.dueDate) : null),
      cell: (w) =>
        w.dueDate ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <LocalDate value={w.dueDate} />
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "created",
      header: "Created",
      hidden: compact,
      sortAccessor: (w) => new Date(w.createdAt),
      cell: (w) => <PersonAt user={w.author} at={w.createdAt} />,
    },
    {
      key: "lastUpdate",
      header: "Last update",
      hidden: compact,
      sortAccessor: (w) => (w.lastUpdate ? new Date(w.lastUpdate.at) : null),
      cell: (w) =>
        w.lastUpdate ? (
          <PersonAt user={w.lastUpdate.user} at={w.lastUpdate.at} />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      hidden: hideBulk,
      cell: (w) => (
        <WorkItemRowActions
          workItemId={w.id}
          actions={w.availableActions}
          currentUserId={currentUserId}
        />
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={300}>
      <DataTable
        columns={columns}
        rows={workItems}
        rowKey={(w) => w.id}
        {...(urlSort ? { sort } : {})}
        emptyState="No work items found."
        selection={
          hideBulk
            ? undefined
            : {
                selected,
                onToggle: (id) =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  }),
                onToggleAll: (visibleIds) =>
                  setSelected((prev) =>
                    prev.size === visibleIds.length && visibleIds.length > 0
                      ? new Set()
                      : new Set(visibleIds),
                  ),
                rowLabel: (w) => `Select work item ${w.globalId}`,
              }
        }
      />

      {!hideBulk && (
        <BulkActions
          selectedIds={Array.from(selected)}
          workItems={workItems}
          users={users}
          // Bulk-assign only ever targets an open release — a closed one is
          // filter-only here.
          releases={releases.filter((r) => r.status === "open")}
          canSeeAll={canSeeAll}
          canOverrideStatus={canOverrideStatus}
          exportSort={
            urlSort?.sort ? `&sort=${urlSort.sort}&dir=${urlSort.dir ?? "asc"}` : undefined
          }
          onClear={() => setSelected(new Set())}
        />
      )}
    </TooltipProvider>
  );
}

function PersonAt({
  user,
  at,
}: {
  user: { id: string; name: string | null } | null;
  at: string;
}) {
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
      <Avatar src={user ? AGENT_AVATAR_BY_USER_ID[user.id] : null} name={user?.name} size={20} />
      <span className="flex flex-col leading-tight">
        <span className="text-foreground">{user?.name ?? "—"}</span>
        <LocalDate value={at} withTime />
      </span>
    </span>
  );
}
